import { Empty, Table, Tooltip } from 'antd';
import type { TableProps, TableColumnType } from 'antd';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAntdTableSortOrder } from '@/utils/antdTableSortOrder';

const EMPTY_TEXT_FILTERED = '該当する検索結果がありません。';
const EMPTY_TEXT_DEFAULT = 'データがありません。';

/** Params that are NOT search/filter criteria — excluded from "has filters" check */
const DEFAULT_NON_FILTER_PARAMS = new Set([
  'page',
  'per_page',
  'sort_by',
  'sort_dir',
  'sort_field',
  'sort_type',
  'order',
  'tab',
]);

export type NonFilterParamsInput = string[] | ((defaults: Set<string>) => Set<string>);

/**
 * A column that may opt OUT of the isAdminSite/enableEllipsis treatment (one nowrap line + tooltip). Set
 * `ellipsisTooltip: false` on a column that clips itself — e.g. one clamped to two lines.
 */
export type AppTableColumn<RecordType = any> = TableColumnType<RecordType> & {
  ellipsisTooltip?: boolean;
};

export type AppTableProps<RecordType extends object = any> = TableProps<RecordType> & {
  recordCount?: number;
  dragTextName?: string;
  nonFilterParams?: NonFilterParamsInput;
  customLabelTotal?: string;
  /** When true, auto-apply single-line ellipsis + tooltip for all cells */
  isAdminSite?: boolean;
  /** Same effect as isAdminSite — enable ellipsis+tooltip for clinic pages */
  enableEllipsis?: boolean;
  /**
   * Opt-in, with isAdminSite / enableEllipsis: mount each cell's ellipsis Tooltip only once
   * the cell is first hovered. Same tooltip, same DOM; for long unpaginated tables, where one
   * Tooltip per text cell (thousands) is a large share of the render.
   */
  lazyEllipsisTooltip?: boolean;
  /** Ref to the inner antd Table (its scrollTo etc.); `ref` itself is the outer wrapper div. */
  tableRef?: React.Ref<any>;
  /** Explicitly override the filtered state instead of deriving it from search params */
  hasFilters?: boolean | null;
};

const ELLIPSIS_TOOLTIP_STYLES = { root: { pointerEvents: 'none' as const, maxWidth: 400 } };
// antd Tooltip's default mouseEnterDelay (0.1s), kept for the first open of a lazy tooltip.
const TOOLTIP_ENTER_DELAY_MS = 100;

const renderEllipsisTooltipTitle = (text: string) => (
  <div style={{ maxHeight: 200, overflowY: 'auto', pointerEvents: 'auto' }}>{text}</div>
);

/**
 * The enableEllipsis cell Tooltip, created on first hover instead of on render. Until then the
 * cell is the same bare <span> the Tooltip would wrap; after that it is the same Tooltip, opened
 * after the same enter delay, then left to antd's own hover handling.
 */
const LazyEllipsisTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [armed, setArmed] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    []
  );

  if (!armed) {
    return (
      <span
        onMouseEnter={() => {
          setArmed(true);
          timerRef.current = window.setTimeout(() => {
            timerRef.current = null;
            // Arming swaps the <span> for the Tooltip's one; a pointer that left before its next
            // move gets no mouseleave on the new node, so ask the DOM instead of trusting events.
            if (spanRef.current?.matches(':hover')) setOpen(true);
          }, TOOLTIP_ENTER_DELAY_MS);
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <Tooltip
      open={open}
      onOpenChange={next => {
        clearTimer();
        setOpen(next);
      }}
      title={renderEllipsisTooltipTitle(text)}
      placement="topLeft"
      styles={ELLIPSIS_TOOLTIP_STYLES}
    >
      {/* Leaving before the first delayed open: antd never saw it open, so it won't call
          onOpenChange(false) — cancel the pending open here instead. */}
      <span ref={spanRef} onMouseLeave={clearTimer}>
        {text}
      </span>
    </Tooltip>
  );
};

const AppTableComponent = React.forwardRef<HTMLDivElement, AppTableProps<any>>(
  ({ ...props }, ref) => {
    const [searchParams] = useSearchParams();

    const excludedParams = useMemo(() => {
      if (!props.nonFilterParams) return DEFAULT_NON_FILTER_PARAMS;
      if (Array.isArray(props.nonFilterParams)) return new Set(props.nonFilterParams);
      return props.nonFilterParams(DEFAULT_NON_FILTER_PARAMS);
    }, [props.nonFilterParams]);

    const hasSearchFilters = Array.from(searchParams.keys()).some(key => !excludedParams.has(key));

    const {
      bordered = true,
      size = 'middle',
      pagination = false,
      scroll,
      columns,
      dataSource,
      recordCount: customRecordCount,
      dragTextName = '一覧',
      styles = {},
      onChange: onChangeExternal,
      locale,
      nonFilterParams: _nonFilterParams,
      customLabelTotal,
      hasFilters: hasFiltersProp = null,
      isAdminSite = false,
      enableEllipsis = false,
      lazyEllipsisTooltip = false,
      tableRef,
      summary,
      ...rest
    } = props;
    // enableEllipsis enables ellipsis+tooltip independently, not tied to isAdminSite
    // (allows clinic pages such as RezeptListTable to use it too).
    const applyEllipsis = isAdminSite || enableEllipsis;
    const isVirtual = !!rest.virtual;

    const hasFilters =
      hasFiltersProp !== null && hasFiltersProp !== undefined ? hasFiltersProp : hasSearchFilters;

    // Internal sort state
    const [activeSortField, setActiveSortField] = useState<string>('');
    const [activeSortOrder, setActiveSortOrder] = useState<'asc' | 'desc'>('asc');

    const styleObject = styles && typeof styles === 'object' ? styles : {};

    const mergedStyles = {
      ...styleObject,
      header: {
        ...styleObject.header,
        cell: {
          whiteSpace: 'nowrap',
          ...styleObject.header?.cell,
        },
      },
    };

    const hasDrag = !!(props.components?.body as any)?.row;

    // Apply sortOrder to columns with sorter: true + ellipsis with tooltip
    const enhancedColumns = useMemo(() => {
      return columns?.map(column => {
        const col = { ...column } as TableColumnType<any>;

        // Add sortOrder for sortable columns (skip if already set externally)
        if (col.sorter === true && col.key && !('sortOrder' in column)) {
          col.sortOrder = getAntdTableSortOrder(
            activeSortField,
            col.key as string,
            activeSortOrder
          );
        }

        // Apply ellipsis + tooltip when applyEllipsis flag is on
        if (!applyEllipsis || (col as any).ellipsisTooltip === false) return col;

        // BD: the table must fit the container and not show horizontal scrolling.
        // When enableEllipsis is enabled, remove col.width from data columns (>= 80px) so
        // Ant Design auto-distributes widths. Overlong cells -> ellipsis + tooltip.
        // Keep widths for small UI columns (checkbox, icon) — width < 80px.
        // A virtual table needs every width as a number (it lays out <div> rows, not <table>),
        // so a caller passing `virtual` supplies the final widths itself and keeps them.
        if (enableEllipsis && !isVirtual && typeof col.width === 'number' && col.width >= 80) {
          delete col.width;
        }

        // Set maxWidth on cell to limit width (admin mode — keep the old behavior)
        if (col.width) {
          const originalOnCell = col.onCell;
          col.onCell = (record: any, index?: number) => {
            const original = originalOnCell ? originalOnCell(record, index) : {};
            return {
              ...original,
              style: {
                maxWidth: col.width,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...(original as any).style,
              },
            };
          };
        }

        // Wrap cell content with single-line ellipsis + scrollable tooltip
        const originalRender = col.render;
        col.ellipsis = { showTitle: false };
        col.render = (value: any, record: any, index: number) => {
          const content = originalRender ? originalRender(value, record, index) : value;
          // Skip tooltip for React elements (e.g. action buttons)
          if (React.isValidElement(content)) return content;
          const text = content == null ? '' : String(content);
          if (lazyEllipsisTooltip) return <LazyEllipsisTooltip text={text} />;
          return (
            <Tooltip
              title={
                <div style={{ maxHeight: 200, overflowY: 'auto', pointerEvents: 'auto' }}>
                  {text}
                </div>
              }
              placement="topLeft"
              styles={{ root: { pointerEvents: 'none', maxWidth: 400 } }}
            >
              <span>{text}</span>
            </Tooltip>
          );
        };

        return col;
      });
    }, [columns, activeSortField, activeSortOrder, applyEllipsis, enableEllipsis, lazyEllipsisTooltip, isVirtual]);

    // Handle table change event
    const handleTableChange = useCallback(
      (pagination: any, filters: any, sorter: any, extra: any) => {
        const sort = Array.isArray(sorter) ? sorter[0] : sorter;
        // Prefer columnKey (stable, equals column.key) over field (which may be dataIndex array)
        const sortField = sort?.columnKey ?? sort?.field;

        // Two-state toggle: ascend ↔ descend (kills antd's 3rd-click null state).
        // On 3rd click, sort.order is null and sort.columnKey is null — flip direction
        // on the remembered field and synthesize a sorter so external handlers update too.
        if (sortField && sort?.order) {
          const sortOrder = sort.order === 'descend' ? 'desc' : 'asc';
          setActiveSortField(String(sortField));
          setActiveSortOrder(sortOrder as 'asc' | 'desc');
          if (onChangeExternal) {
            onChangeExternal(pagination, filters, sorter, extra);
          }
        } else if (activeSortField) {
          const flippedOrder: 'asc' | 'desc' = activeSortOrder === 'asc' ? 'desc' : 'asc';
          setActiveSortOrder(flippedOrder);
          if (onChangeExternal) {
            const flippedSorter = {
              ...(sort || {}),
              columnKey: activeSortField,
              field: activeSortField,
              order: flippedOrder === 'desc' ? 'descend' : 'ascend',
            };
            onChangeExternal(pagination, filters, flippedSorter, extra);
          }
        } else if (onChangeExternal) {
          onChangeExternal(pagination, filters, sorter, extra);
        }
      },
      [onChangeExternal, activeSortField, activeSortOrder]
    );

    return (
      <div ref={ref} className={`app-table-ra2n${summary ? ' app-table-ra2n-has-summary' : ''}`}>
        {customRecordCount !== undefined && (
          <div className="mb-4 text-sm text-gray-600">
            {customLabelTotal || '該当件数'}: {customRecordCount}件
          </div>
        )}
        {hasDrag && (
          <div className="text-red-600 text-[14px] mb-4">
            【≡】アイコンのドラッグ＆ドロップ操作で{dragTextName}の表示順を変更可能です
          </div>
        )}
        <Table
          ref={tableRef}
          bordered={bordered}
          size={size}
          pagination={pagination}
          showSorterTooltip={false}
          scroll={scroll ?? { x: true }}
          dataSource={dataSource}
          columns={enhancedColumns}
          styles={mergedStyles}
          onChange={handleTableChange}
          summary={summary}
          {...rest}
          locale={
            locale ?? {
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={hasFilters ? EMPTY_TEXT_FILTERED : EMPTY_TEXT_DEFAULT}
                />
              ),
            }
          }
        />
      </div>
    );
  }
);

AppTableComponent.displayName = 'AppTable';

const AppTable = AppTableComponent as <RecordType extends object = any>(
  props: AppTableProps<RecordType> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export default AppTable;
